// =====================================================================
// WeddingOS – Supabase Service
// =====================================================================
// • Singleton-Client
// • Realtime-Subscription-Helper für alle Live-Tabellen
// • Wrapper für die im Schema definierten RPCs (Gast & Staff)
// • Storage-Helper für Foto-Uploads
// =====================================================================

import { createClient } from "@supabase/supabase-js";

const SUPABASE_URL      = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;

let supabaseClient = null;
if (!SUPABASE_URL || !SUPABASE_ANON_KEY) {
  console.warn("Missing Supabase env vars (VITE_SUPABASE_URL / VITE_SUPABASE_ANON_KEY). Supabase client is not initialized.");
} else {
  supabaseClient = createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
    auth: {
      persistSession:    true,
      autoRefreshToken:  true,
      detectSessionInUrl: true,
    },
    realtime: { params: { eventsPerSecond: 10 } },
  });
}

export const supabase = supabaseClient;


// =====================================================================
// REALTIME
// =====================================================================

/**
 * Generischer Subscription-Helper.
 * Gibt eine cleanup-Funktion zurück (z.B. für useEffect).
 *
 * @param {string} table       – Tabellenname (public.<table>)
 * @param {string|null} weddingId – wenn gesetzt, wird per filter eingeschränkt
 * @param {(payload) => void} onChange
 */
export function subscribeTable(table, weddingId, onChange) {
  const channel = supabase
    .channel(`rt:${table}:${weddingId ?? "all"}:${crypto.randomUUID()}`)
    .on(
      "postgres_changes",
      {
        event:  "*",
        schema: "public",
        table,
        ...(weddingId ? { filter: `wedding_id=eq.${weddingId}` } : {}),
      },
      (payload) => onChange(payload),
    )
    .subscribe();

  return () => {
    supabase.removeChannel(channel);
  };
}

// Convenience-Wrapper pro Tabelle
export const realtime = {
  musicQueue:    (weddingId, cb) => subscribeTable("music_requests",    weddingId, cb),
  drinkOrders:   (weddingId, cb) => subscribeTable("drink_orders",      weddingId, cb),
  drinkMenu:     (weddingId, cb) => subscribeTable("drink_menu_items",  weddingId, cb),
  photos:        (weddingId, cb) => subscribeTable("photos",            weddingId, cb),
  guestbook:     (weddingId, cb) => subscribeTable("guestbook_entries", weddingId, cb),
  announcements: (weddingId, cb) => subscribeTable("announcements",     weddingId, cb),
};

// =====================================================================
// AUTH (Brautpaar)
// =====================================================================

export async function signUpCouple(email, password) {
  const { data, error } = await supabase.auth.signUp({
    email,
    password,
    // Double-Opt-In: Bestätigungslink führt zurück in die App (?confirmed=1 → Danke-Screen)
    options: { emailRedirectTo: `${window.location.origin}/login?confirmed=1` },
  });
  if (error) throw error;
  return data;
}

export async function signInCouple(email, password) {
  const { data, error } = await supabase.auth.signInWithPassword({ email, password });
  if (error) throw error;
  return data;
}

export async function signOut() {
  const { error } = await supabase.auth.signOut();
  if (error) throw error;
}

export async function getSession() {
  const { data, error } = await supabase.auth.getSession();
  if (error) throw error;
  return data.session;
}

// =====================================================================
// GAST-RPCs (Token-basiert)
// =====================================================================

export async function guestJoin(token) {
  const { data, error } = await supabase.rpc("guest_join", { p_token: token });
  if (error) throw error;
  // Funktion gibt eine Zeile zurück
  return Array.isArray(data) ? data[0] : data;
}

export async function submitMusicRequest({ guestId, track, message }) {
  const { data, error } = await supabase.rpc("submit_music_request", {
    p_guest_id:         guestId,
    p_spotify_track_id: track.id,
    p_track_uri:        track.uri,
    p_title:            track.title,
    p_artist:           track.artist,
    p_cover_url:        track.cover,
    p_message:          message ?? "",
  });
  if (error) throw error;
  return data;
}

export async function submitDrinkOrder({ guestId, drinkName, note, quantity = 1 }) {
  const { data, error } = await supabase.rpc("submit_drink_order", {
    p_guest_id:   guestId,
    p_drink_name: drinkName,
    p_note:       note ?? "",
    p_quantity:   quantity,
  });
  if (error) throw error;
  return data;
}

// Brautpaar-Gast holen/anlegen (für eigene Bestellung / eigenen Musikwunsch aus dem Dashboard)
export async function getCoupleGuest(weddingId) {
  const { data, error } = await supabase.rpc("dashboard_couple_guest", { p_wedding_id: weddingId });
  if (error) throw error;
  return Array.isArray(data) ? data[0] : data;
}

export async function submitGuestbookEntry({ guestId, message, photoUrl }) {
  const { data, error } = await supabase.rpc("submit_guestbook_entry", {
    p_guest_id:  guestId,
    p_message:   message,
    p_photo_url: photoUrl ?? "",
  });
  if (error) throw error;
  return data;
}

// Delegierte Gast-Ankündigung (Trauzeuge)
export async function submitAnnouncement({ guestId, message }) {
  const { data, error } = await supabase.rpc("submit_announcement", {
    p_guest_id: guestId,
    p_message:  message,
  });
  if (error) throw error;
  return data;
}

export async function guestCanAnnounce(guestId) {
  const { data, error } = await supabase.rpc("guest_can_announce", { p_guest_id: guestId });
  if (error) throw error;
  return data === true;
}

// =====================================================================
// STAFF-RPCs
// =====================================================================

export async function djDecideRequest({ token, requestId, approve }) {
  const { data, error } = await supabase.rpc("dj_decide_request", {
    p_token:      token,
    p_request_id: requestId,
    p_approve:    approve,
  });
  if (error) throw error;
  return data;
}

export async function barUpdateStatus({ token, orderId, status }) {
  const { data, error } = await supabase.rpc("bar_update_status", {
    p_token:    token,
    p_order_id: orderId,
    p_status:   status, // 'ordered' | 'preparing' | 'ready' | 'delivered'
  });
  if (error) throw error;
  return data;
}

// Kellner nimmt eine Bestellung pro Tisch auf (kein Gast, nur Tischnummer)
export async function barAddOrder({ token, tableNumber, drinkName, quantity = 1, note }) {
  const { data, error } = await supabase.rpc("bar_add_order", {
    p_token:        token,
    p_table_number: tableNumber ?? "",
    p_drink_name:   drinkName,
    p_quantity:     quantity,
    p_note:         note ?? "",
  });
  if (error) throw error;
  return data;
}

// Bestellung stornieren (falsche Kellner-/Gast-Bestellung löschen)
export async function barDeleteOrder({ token, orderId }) {
  const { error } = await supabase.rpc("bar_delete_order", { p_token: token, p_order_id: orderId });
  if (error) throw error;
}

// Getränk als ausverkauft/verfügbar markieren ("Ausgetrunken")
export async function barSetAvailable({ token, itemId, available }) {
  const { data, error } = await supabase.rpc("bar_set_available", {
    p_token:     token,
    p_item_id:   itemId,
    p_available: available,
  });
  if (error) throw error;
  return data;
}

// Distinct Tische der Hochzeit ({table_number, table_label}) – für Kellner-Tischwahl
export async function barGetTables(token) {
  const { data, error } = await supabase.rpc("bar_get_tables", { p_token: token });
  if (error) throw error;
  return data ?? [];
}

// Getränkekarte token-basiert (Bar-Screen kennt keine wedding_id)
export async function barGetMenu(token) {
  const { data, error } = await supabase.rpc("bar_get_menu", { p_token: token });
  if (error) throw error;
  return data ?? [];
}

// =====================================================================
// LESEN (für Gast-/Staff-Views)
// =====================================================================

export async function fetchDrinkMenu(weddingId) {
  const { data, error } = await supabase
    .from("drink_menu_items")
    .select("*")
    .eq("wedding_id", weddingId)
    .order("sort_order", { ascending: true });
  if (error) throw error;
  return data ?? [];
}

export async function fetchAnnouncements(weddingId, limit = 10) {
  const { data, error } = await supabase
    .from("announcements")
    .select("*")
    .eq("wedding_id", weddingId)
    .order("created_at", { ascending: false })
    .limit(limit);
  if (error) throw error;
  return data ?? [];
}

export async function fetchPhotos(weddingId, limit = 100) {
  const { data, error } = await supabase
    .from("photos")
    .select("*")
    .eq("wedding_id", weddingId)
    .eq("approved", true)
    .order("created_at", { ascending: false })
    .limit(limit);
  if (error) throw error;
  return data ?? [];
}

export async function fetchGuestbook(weddingId, limit = 200) {
  const { data, error } = await supabase
    .from("guestbook_entries")
    .select("*")
    .eq("wedding_id", weddingId)
    .order("created_at", { ascending: false })
    .limit(limit);
  if (error) throw error;
  return data ?? [];
}

export async function fetchMusicQueue(weddingId, status = null) {
  let q = supabase.from("music_requests").select("*, guest:guests(name, table_number)").eq("wedding_id", weddingId);
  if (status) q = q.eq("status", status);
  const { data, error } = await q.order("created_at", { ascending: true });
  if (error) throw error;
  return data ?? [];
}

export async function fetchOpenDrinkOrders(weddingId) {
  const { data, error } = await supabase
    .from("drink_orders")
    .select("*, guest:guests(name, table_number)")
    .eq("wedding_id", weddingId)
    .neq("status", "delivered")
    .order("created_at", { ascending: true });
  if (error) throw error;
  return data ?? [];
}

// =====================================================================
// STORAGE (Fotos)
// =====================================================================

/**
 * Verkleinert ein Bild vor dem Upload (spart Storage + Bandbreite).
 * Skaliert auf max. 2000px lange Kante und re-encodet als JPEG q0.8.
 * Fällt bei Fehler / Nicht-Bild / kein Gewinn auf die Originaldatei zurück.
 */
async function compressImage(file, { maxDim = 2000, quality = 0.8 } = {}) {
  if (!file?.type?.startsWith("image/")) return file;
  try {
    const bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
    const scale  = Math.min(1, maxDim / Math.max(bitmap.width, bitmap.height));
    const w = Math.round(bitmap.width  * scale);
    const h = Math.round(bitmap.height * scale);

    const canvas = document.createElement("canvas");
    canvas.width = w; canvas.height = h;
    canvas.getContext("2d").drawImage(bitmap, 0, 0, w, h);
    bitmap.close?.();

    const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", quality));
    if (!blob || blob.size >= file.size) return file; // kein Gewinn → Original behalten
    return new File([blob], file.name.replace(/\.[^.]+$/, "") + ".jpg", { type: "image/jpeg" });
  } catch {
    return file; // z.B. HEIC ohne Decoder → Original hochladen
  }
}

/**
 * Lädt eine Datei in den Photo-Bucket und registriert sie via RPC
 * in der DB. Pfadschema: <weddingId>/<timestamp>_<random>.<ext>
 */
export async function uploadPhoto({ guestId, weddingId, file, comment, albumId }) {
  file = await compressImage(file);
  const ext = file.name.split(".").pop()?.toLowerCase() ?? "jpg";

  // 1. Signierte Upload-URL von R2 holen (prüft Gast + Foto-Limit)
  const { data: pre, error: preErr } = await supabase.functions.invoke("r2-presign", {
    body: { weddingId, guestId, ext },
  });
  if (preErr) throw preErr;
  if (pre?.error) throw new Error(pre.error);

  // 2. Datei direkt zu R2 hochladen
  const put = await fetch(pre.uploadUrl, {
    method: "PUT",
    body: file,
    headers: { "Content-Type": file.type || "image/jpeg" },
  });
  if (!put.ok) throw new Error("r2_upload_failed");

  // 3. In DB registrieren
  const { data, error } = await supabase.rpc("register_photo", {
    p_guest_id:     guestId,
    p_storage_path: pre.path,
    p_url:          pre.publicUrl,
    p_comment:      comment ?? "",
    p_album_id:     albumId ?? null,
  });
  if (error) throw error;
  return data;
}

// =====================================================================
// SITE CONTENT (editierbare Marketing-Texte)
// =====================================================================

// Lädt alle site_content-Zeilen (öffentlich lesbar). Map key → value.
// Optional: nur eine Seite (page) laden.
export async function getSiteContent(page = null) {
  if (!supabase) return {};
  let q = supabase.from("site_content").select("key, value");
  if (page) q = q.eq("page", page);
  const { data, error } = await q;
  if (error) throw error;
  return Object.fromEntries((data ?? []).map((r) => [r.key, r.value]));
}

// Wie getSiteContent, aber mit allen Spalten (für den Admin-Editor: label, page, sort).
export async function getSiteContentRows() {
  if (!supabase) return [];
  const { data, error } = await supabase
    .from("site_content")
    .select("key, value, label, page, sort, updated_at")
    .order("page", { ascending: true })
    .order("sort", { ascending: true });
  if (error) throw error;
  return data ?? [];
}

// Admin: einen Text setzen (Gate via is_admin() im RPC).
export async function adminSetContent(key, value) {
  const { data, error } = await supabase.rpc("admin_set_content", {
    p_key: key, p_value: value,
  });
  if (error) throw error;
  return data;
}
